/**
 * Profile Page
 * 
 * User profile page with account information, access level, stats, and notification settings
 */

import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageContainer } from '../components/UI/PageContainer';
import { PageHeader } from '../components/UI/PageHeader';
import { Card } from '../components/UI/Card';
import { Button } from '../components/UI/Button';
import { Input } from '../components/UI/Input';
import { Badge } from '../components/UI/Badge';
import { useAuthContext } from '../components/Auth/AuthProvider';
import { getUser, getCurrentUserProfile, updateUser, type UserDetail } from '../api/users';
import { NotificationPreferences } from '../components/Notifications/NotificationPreferences';
import { useWorkspace } from '../hooks/useWorkspace';
import { usePermissions } from '../hooks/usePermissions';
import { 
  User, 
  Mail, 
  Shield, 
  Calendar, 
  CheckCircle, 
  XCircle, 
  Edit3, 
  Save, 
  X,
  Bell,
  Database,
  Folder,
  Activity,
  BarChart3,
} from 'lucide-react';
import { useToastStore } from '../stores/toast';
import { format } from 'date-fns';
import { TraceKitConfig } from '../types/config';

type ProfileTab = 'overview' | 'notifications' | 'privacy';

interface ProfileProps {
  config?: TraceKitConfig;
}

export function Profile({ config }: ProfileProps = {}) {
  const { user: authUser } = useAuthContext();
  const { tenant, project, tenantId, projectId } = useWorkspace();
  const { permissions, roles, hasPermission } = usePermissions();
  const { addToast } = useToastStore();
  const queryClient = useQueryClient();
  
  // Check if we're in keystone-rfp package (hide Privacy tab)
  const isKeystoneRfp = window.location.pathname.includes('keystone-rfp') || 
                        config?.branding?.appName?.toLowerCase().includes('keystone');
  
  const [activeTab, setActiveTab] = useState<ProfileTab>('overview');
  
  // If privacy tab is hidden and user is on privacy tab, switch to overview
  useEffect(() => {
    if (isKeystoneRfp && activeTab === 'privacy') {
      setActiveTab('overview');
    }
  }, [isKeystoneRfp, activeTab]);
  const [isEditing, setIsEditing] = useState(false);
  const [editedUser, setEditedUser] = useState({
    full_name: '',
    email: '',
    username: '',
  });

  // Fetch user profile
  const { data: userProfile, isLoading, error } = useQuery({
    queryKey: ['user-profile', authUser?.id],
    queryFn: async () => {
      if (authUser?.id) {
        try {
          // Use getCurrentUserProfile which calls /users/me
          return await getCurrentUserProfile();
        } catch (error: any) {
          // Fallback to getUser if /users/me fails (for backward compatibility)
          if (error?.response?.status === 404 || error?.response?.status === 405 || error?.response?.status === 500) {
            console.log('[Profile] getCurrentUserProfile failed, falling back to getUser');
            return await getUser(authUser.id);
          }
          throw error;
        }
      }
      return null;
    },
    enabled: !!authUser?.id,
  });

  // Update user mutation
  const updateMutation = useMutation({
    mutationFn: (data: { full_name?: string; email?: string; username?: string }) => 
      updateUser(authUser!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
      setIsEditing(false);
      addToast({
        type: 'success',
        message: 'Profile updated successfully',
      });
    },
    onError: (error: any) => {
      addToast({
        type: 'error',
        message: error?.response?.data?.detail || error?.message || 'Failed to update profile',
      });
    },
  });

  const handleEdit = () => {
    if (userProfile) {
      setEditedUser({
        full_name: userProfile.full_name || '',
        email: userProfile.email || '',
        username: userProfile.username || '',
      });
      setIsEditing(true);
    }
  };

  const handleSave = () => {
    updateMutation.mutate(editedUser);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedUser({
      full_name: '',
      email: '',
      username: '',
    });
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Card className="max-w-4xl mx-auto mt-8">
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading profile...</p>
          </div>
        </Card>
      </PageContainer>
    );
  }

  if (error || !userProfile) {
    return (
      <PageContainer>
        <Card className="max-w-4xl mx-auto mt-8">
          <div className="text-center py-12">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Error Loading Profile
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              {error instanceof Error ? error.message : 'Failed to load user profile'}
            </p>
          </div>
        </Card>
      </PageContainer>
    );
  }

  // Calculate access level
  const accessLevel = roles.length > 0 
    ? roles[0].display_name || roles[0].name
    : hasPermission('data_management.read') 
      ? 'Admin'
      : hasPermission('proposals.write')
        ? 'Editor'
        : 'Viewer';

  // Count permissions
  const permissionCount = permissions.length;
  const tenantCount = userProfile.tenant_assignments?.length || 0;
  const projectCount = userProfile.project_assignments?.length || 0;

  return (
    <PageContainer>
      <div className="max-w-6xl mx-auto mt-8">
        <PageHeader
          icon={<User />}
          title="My Profile"
          subtitle="Manage your account settings and preferences"
        />

        {/* Tabs */}
        <div className="border-b border-gray-200 dark:border-slate-700 mb-6">
          <div className="flex gap-4">
            {[
              { id: 'overview' as ProfileTab, label: 'Overview', icon: User },
              { id: 'notifications' as ProfileTab, label: 'Notifications', icon: Bell },
              // Hide Privacy tab for keystone-rfp package
              ...(window.location.pathname.includes('keystone-rfp') || 
                  config?.branding?.appName?.toLowerCase().includes('keystone') ? [] : 
                  [{ id: 'privacy' as ProfileTab, label: 'Privacy', icon: Shield }]),
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                    activeTab === tab.id
                      ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                      : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Profile Information */}
            <Card>
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                    Profile Information
                  </h2>
                  {!isEditing && (
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      <Edit3 className="h-4 w-4 mr-2" />
                      Edit Profile
                    </Button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Full Name
                      </label>
                      <Input
                        value={editedUser.full_name}
                        onChange={(e) => setEditedUser({ ...editedUser, full_name: e.target.value })}
                        placeholder="Enter your full name"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Email
                      </label>
                      <Input
                        type="email"
                        value={editedUser.email}
                        onChange={(e) => setEditedUser({ ...editedUser, email: e.target.value })}
                        placeholder="Enter your email"
                        className="w-full"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Username
                      </label>
                      <Input
                        value={editedUser.username}
                        onChange={(e) => setEditedUser({ ...editedUser, username: e.target.value })}
                        placeholder="Enter your username"
                        className="w-full"
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <Button variant="outline" onClick={handleCancel} disabled={updateMutation.isPending}>
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <Button onClick={handleSave} disabled={updateMutation.isPending}>
                        <Save className="h-4 w-4 mr-2" />
                        {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Full Name</div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {userProfile.full_name || 'Not set'}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Email</div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        {userProfile.email}
                        {userProfile.is_email_verified ? (
                          <Badge variant="success" className="text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </Badge>
                        ) : (
                          <Badge variant="warning" className="text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Unverified
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Username</div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {userProfile.username}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">User ID</div>
                      <div className="font-mono text-sm text-gray-700 dark:text-gray-300">
                        {userProfile.id}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Account Status</div>
                      <div className="flex items-center gap-2">
                        {userProfile.is_active ? (
                          <Badge variant="success">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge variant="error">
                            <XCircle className="h-3 w-3 mr-1" />
                            Inactive
                          </Badge>
                        )}
                      </div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Member Since</div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {format(new Date(userProfile.created_at), 'MMMM d, yyyy')}
                      </div>
                    </div>
                    {userProfile.last_login_at && (
                      <div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Last Login</div>
                        <div className="font-semibold text-gray-900 dark:text-gray-100">
                          {format(new Date(userProfile.last_login_at), 'MMMM d, yyyy HH:mm')}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>

            {/* Access Level & Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Shield className="h-5 w-5 text-blue-600" />
                    Access Level
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Current Role</div>
                      <Badge variant="info" className="text-base px-3 py-1">
                        {accessLevel}
                      </Badge>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Permissions</div>
                      <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                        {permissionCount}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Active permissions
                      </div>
                    </div>
                    {roles.length > 0 && (
                      <div>
                        <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Roles</div>
                        <div className="flex flex-wrap gap-2">
                          {roles.map((role, idx) => (
                            <Badge key={idx} variant="default">
                              {role.display_name || role.name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </Card>

              <Card>
                <div className="p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-green-600" />
                    Statistics
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Database className="h-4 w-4 text-blue-600" />
                        <div className="text-xs text-gray-600 dark:text-gray-400">Tenants</div>
                      </div>
                      <div className="text-2xl font-bold text-blue-900 dark:text-blue-300">
                        {tenantCount}
                      </div>
                    </div>
                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Folder className="h-4 w-4 text-purple-600" />
                        <div className="text-xs text-gray-600 dark:text-gray-400">Projects</div>
                      </div>
                      <div className="text-2xl font-bold text-purple-900 dark:text-purple-300">
                        {projectCount}
                      </div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Tenant & Project Assignments */}
            {(userProfile.tenant_assignments?.length > 0 || userProfile.project_assignments?.length > 0) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {userProfile.tenant_assignments && userProfile.tenant_assignments.length > 0 && (
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                        <Database className="h-5 w-5 text-blue-600" />
                        Tenant Assignments
                      </h3>
                      <div className="space-y-3">
                        {userProfile.tenant_assignments.map((assignment, idx) => (
                          <div key={idx} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                              {assignment.tenant_name}
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              {assignment.role_name && (
                                <Badge variant="info" className="text-xs">
                                  {assignment.role_name}
                                </Badge>
                              )}
                              <Badge 
                                variant={assignment.status === 'accepted' ? 'success' : 'warning'}
                                className="text-xs"
                              >
                                {assignment.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}

                {userProfile.project_assignments && userProfile.project_assignments.length > 0 && (
                  <Card>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
                        <Folder className="h-5 w-5 text-purple-600" />
                        Project Assignments
                      </h3>
                      <div className="space-y-3">
                        {userProfile.project_assignments.map((assignment, idx) => (
                          <div key={idx} className="p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                            <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">
                              {assignment.project_name}
                            </div>
                            <div className="flex items-center gap-2 text-sm">
                              {assignment.role_name && (
                                <Badge variant="info" className="text-xs">
                                  {assignment.role_name}
                                </Badge>
                              )}
                              <Badge 
                                variant={assignment.status === 'accepted' ? 'success' : 'warning'}
                                className="text-xs"
                              >
                                {assignment.status}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </Card>
                )}
              </div>
            )}
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <Card>
            <div className="p-6">
              <NotificationPreferences projectId={projectId} />
            </div>
          </Card>
        )}

        {/* Privacy Tab */}
        {activeTab === 'privacy' && (
          <div className="space-y-6">
            <Card>
              <div className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
                  Privacy Controls
                </h2>
                <div className="space-y-4">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Profile visibility to other users
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Control who can see your profile information
                        </p>
                      </div>
                      <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-slate-700 text-sm">
                        <option value="public">Public</option>
                        <option value="team">Team Only</option>
                        <option value="private">Private</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Activity tracking preferences
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Allow activity tracking for analytics
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        defaultChecked
                        className="w-5 h-5"
                      />
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Data sharing permissions
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Allow data sharing for improved features
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        defaultChecked
                        className="w-5 h-5"
                      />
                    </div>
                  </div>

                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <label className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          Analytics participation
                        </label>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          Help improve the platform by sharing usage analytics
                        </p>
                      </div>
                      <input
                        type="checkbox"
                        defaultChecked
                        className="w-5 h-5"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
    </PageContainer>
  );
}
